import axios from '../utils/axios';
import { sortConfigsByLetter } from '../utils/configSort';
import { MultiGateGroup, MultiGateSyncResult } from '../types/MultiGate';

export async function getSessions(queryString: string): Promise<any[]> {
  return axios.get(`/sessions?${queryString}`).then((res) => res.data);
}

export async function updateSession(sessionId: string, data: any): Promise<any> {
  return axios.put(`/sessions/${sessionId}`, data).then((res) => res.data);
}

export async function createSession(data: any): Promise<any> {
  return axios.post('/sessions', data).then((res) => res.data);
}

export async function deleteSession(sessionId: string): Promise<any> {
  return axios.delete(`/sessions/${sessionId}`).then((res) => res.data);
}

// Bespoke requests

export async function getSessionCalcData(
  sessionId: string,
  opts?: { includeRolling?: boolean },
): Promise<any> {
  // `include=rolling` opts back into the tyre-plot-only delta variants
  // (deltas_rolling / deltas_traverse_rolling / deltas_self), which the server
  // omits by default. run_pipeline_tracker is always dropped server-side.
  const include = opts?.includeRolling ? '&include=rolling' : '';
  return axios.get(`/sessions/${sessionId}?scope=calculated_data_internal${include}`).then((res) => {
    // Sort configs array if it exists
    if (res.data && Array.isArray(res.data.configs)) {
      res.data.configs = sortConfigsByLetter(res.data.configs);
    }
    return res.data;
  });
}

// Lightweight "phase 1" payload for the SessionView config/run list: same shape
// as calculated_data_internal but without the heavy per-channel arrays (traverse,
// deltas_*) that only the plots need. Keeps average/deltas/run_health_obj/solve so
// the list can show drag/delta/health without waiting for the full payload.
export async function getSessionSummary(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=calculated_data_summary`).then((res) => {
    if (res.data && Array.isArray(res.data.configs)) {
      res.data.configs = sortConfigsByLetter(res.data.configs);
    }
    return res.data;
  });
}

// Minimal payload for the SessionSimple page (freeride/discarded singletons):
// session + its single config + light runs (health/datetime/description only).
export async function getSessionSimple(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=simple`).then((res) => res.data);
}

// Returns the SessionSingletons mapping keyed by singleton name
// (discarded / discarded_archive / reference_calibrations / freeride_unstructured).
export async function getSingletonSessions(): Promise<Record<string, any>> {
  return axios.get('/sessions?scope=singletons').then((res) => res.data);
}

export async function getSessionCSV(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=csv`, { responseType: 'blob' });
}

export async function getSessionXLS(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=xls`, { responseType: 'blob' });
}

export async function getSessionReport(sessionId: string): Promise<any> {
  return axios.get(`/test-sessions/${sessionId}?scope=xls_report`, { responseType: 'blob' });
}

export async function getTyreReferenceSessions(): Promise<any[]> {
  return axios.get('/sessions?scope=tyre_reference_sessions').then((res) => res.data);
}

export async function getTyreReport(sessionId: string): Promise<any[]> {
  return axios.get(`/sessions/${sessionId}?scope=tyre_report`).then((res) => {
    const sessions = res.data;
    if (!Array.isArray(sessions) || sessions.length <= 1) {
      return sessions;
    }

    // First session is the reference test, keep it first
    const referenceTest = sessions[0];
    const otherTests = sessions.slice(1);

    // Sort other tests by date in descending order (most recent first)
    otherTests.sort((a, b) => {
      const dateA = new Date(a.date || a.created_at).getTime();
      const dateB = new Date(b.date || b.created_at).getTime();
      return dateA - dateB; // ascending order
    });

    return [referenceTest, ...otherTests];
  });
}

/**
 * Composite data packet backing the Advanced > Tyre Report page. One object holding
 * the raw responses of every read the page makes, so a report can be reproduced
 * offline. Session ids scope the rolling-radii fetch to the report's session family
 * (reference session plus its comparison sessions).
 */
export interface TyreReportDataExport {
  exported_at: string;
  reference_session_id: string;
  session_ids: string[];
  tyre_report: any;
  tyre_reference_sessions: any;
  equipment_catalog: any;
  rolling_radii_plotting_data: any;
}

export async function getTyreReportDataExport(
  referenceSessionId: string,
  sessionIds: string[],
): Promise<TyreReportDataExport> {
  let rollingRadiiUrl = '/equipment-team-rolling-radii?scope=plotting_data';
  if (sessionIds.length > 0) {
    rollingRadiiUrl += `&session_ids=${encodeURIComponent(sessionIds.join(','))}`;
  }
  const [tyreReport, referenceSessions, equipmentCatalog, rollingRadii] = await Promise.all([
    axios.get(`/sessions/${referenceSessionId}?scope=tyre_report`),
    axios.get('/sessions?scope=tyre_reference_sessions'),
    axios.get('/equipment-catalog'),
    axios.get(rollingRadiiUrl),
  ]);
  return {
    exported_at: new Date().toISOString(),
    reference_session_id: referenceSessionId,
    session_ids: sessionIds,
    tyre_report: tyreReport.data,
    tyre_reference_sessions: referenceSessions.data,
    equipment_catalog: equipmentCatalog.data,
    rolling_radii_plotting_data: rollingRadii.data,
  };
}

export async function updateWeatherData(sessionId: string): Promise<any> {
  return axios.get(`/sessions/${sessionId}?scope=update_weather`).then((res) => res.data);
}

export async function getTyreReportExcel(
  sessionId: string,
  refSpeed?: number,
  psi: boolean = false,
  nearestPair: boolean = false,
): Promise<Blob> {
  let url = `/sessions/${sessionId}?scope=tyre_report_excel&psi=${psi}&nearest_pair=${nearestPair}`;
  if (refSpeed !== undefined) {
    url += `&ref_speed=${refSpeed}`;
  }
  return axios.get(url, { responseType: 'blob' }).then((res) => res.data);
}

// ── Session Command Endpoints (POST) ──────────────────────

// Multi-gate (Advanced → Multi-gate). The group is answered for the reference,
// whichever member's id is passed.
export async function getMultiGateReferenceSessions(): Promise<any[]> {
  return axios.get('/sessions?scope=multigate_reference_sessions').then((res) => res.data);
}

export async function getMultiGate(sessionId: string): Promise<MultiGateGroup> {
  return axios.get(`/sessions/${sessionId}/multigate`).then((res) => res.data);
}

export async function setMultiGateGates(sessionId: string, gatesIds: string[]): Promise<MultiGateGroup> {
  return axios.post(`/sessions/${sessionId}/multigate`, { gates_ids: gatesIds }).then((res) => res.data);
}

// Swap a comparison's gate onto the reference session, which then re-calibrates on it.
export async function setMultiGateReference(sessionId: string, gatesId: string): Promise<MultiGateGroup> {
  return axios.post(`/sessions/${sessionId}/multigate/reference`, { gates_id: gatesId }).then((res) => res.data);
}

export async function syncMultiGate(sessionId: string): Promise<MultiGateSyncResult> {
  return axios.post(`/sessions/${sessionId}/multigate/sync`).then((res) => res.data);
}

export async function duplicateSession(
  sessionId: string,
  mode: 'with_runs' | 'without_runs',
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/duplicate`, { mode })
    .then((res) => res.data);
}

export async function setWheelAndReprocess(
  sessionId: string,
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/set-wheel-and-reprocess`)
    .then((res) => res.data);
}

export async function assignCrr(
  sessionId: string,
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/assign-crr`)
    .then((res) => res.data);
}

export async function reorderConfigs(
  sessionId: string,
  body: Record<string, any>,
): Promise<any> {
  return axios
    .post(`/sessions/${sessionId}/reorder`, body)
    .then((res) => res.data);
}
